<template>
  <div class="orangehrm-sign-contract-container">
    <div class="orangehrm-sign-contract-wrapper">
      <div class="pdf-container"></div>
      <div ref="orangehrmCardContainer" class="orangehrm-card-container">
        <div
          v-if="!isCompactLayout"
          class="toggle-sidebar"
          @click="toggleSideBar"
        >
          <oxd-icon class="orangehrm-file-icon" name="list" />
        </div>
        <oxd-form class="pdf-sign-container">
          <div v-if="!pdf && !isLoading && !hasNewContract">
            <h4 style="text-align: center">
              {{ $t('auth.preview_sign_contract') }}
            </h4>
            <oxd-input-field
              v-model="contractFile"
              type="file"
              accept="application/pdf"
              :button-label="$t('general.browse')"
              :placeholder="$t('general.no_file_selected')"
              @change="handleFileUpload"
            />
          </div>
          <div class="tools-container" :class="{open: isSidebarOpen}">
            <div v-if="pdf">
              <div class="sig-tools">
                <div class="sig-top">
                  <h3 class="sig-title">{{ $t('auth.contract_e_sign') }}</h3>
                  <oxd-icon-button
                    v-if="isMobile"
                    class="button-canvas"
                    name="x"
                    @click="toggleSideBar"
                  ></oxd-icon-button>
                </div>
                <div class="spacer"></div>
              </div>

              <p class="sig-title-draw">{{ $t('auth.contract_draw_sign') }}</p>
              <div class="tool-area">
                <!-- Draw mode -->
                <template v-if="true">
                  <canvas
                    ref="drawCanvas"
                    class="draw-canvas"
                    @mousedown="startDrawing"
                    @mousemove="draw"
                    @mouseup="stopDrawing"
                    @mouseleave="stopDrawing"
                    @touchstart="startDrawing"
                    @touchmove="draw"
                    @touchend="stopDrawing"
                    @touchcancel="stopDrawing"
                  ></canvas>
                  <div class="button-container">
                    <oxd-icon-button
                      ref="addSignatureButton"
                      class="button-canvas"
                      name="plus-lg"
                      @click="addDrawnSignature"
                    ></oxd-icon-button>
                    <oxd-icon-button
                      class="button-canvas"
                      name="trash"
                      @click="clearCanvas"
                    ></oxd-icon-button>
                  </div>
                </template>
              </div>
              <div class="spacer"></div>
              <p class="sig-title-draw">{{ $t('auth.contract_image_sign') }}</p>
              <div class="tool-area">
                <!-- Upload mode -->
                <template v-if="true">
                  <oxd-input-field
                    v-model="signatureImageFile"
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    :button-label="$t('general.browse')"
                    :placeholder="$t('general.no_file_selected')"
                    @change="handleImageUpload"
                  />
                </template>
              </div>

              <div class="button-action">
                <oxd-button
                  display-type="main"
                  size="medium"
                  :label="$t('general.download')"
                  type="button"
                  @click="downloadPDF"
                />
                <oxd-button
                  display-type="secondary"
                  size="medium"
                  :label="$t('general.submit')"
                  type="button"
                  @click="submitPDF"
                />
              </div>
            </div>
          </div>

          <div v-show="isLoading" class="orangehrm-loader">
            <oxd-loading-spinner />
          </div>

          <div v-show="!isLoading" ref="pdfViewport" class="content-pdf">
            <div v-if="pdf" ref="pdfScrollSurface" class="pdf-scroll-surface">
              <div
                class="canvas-container"
                :class="{
                  panning: isPanning,
                  zooming: isZooming,
                }"
              >
                <canvas ref="pdfCanvas" class="pdf-canvas"></canvas>
                <canvas
                  ref="overlayCanvas"
                  class="overlay-canvas"
                  :class="{
                    clickable: zoomFactor <= 1 && !isPanning && !isZooming,
                  }"
                  @click="handleOverlayClick"
                  @touchstart="handleOverlayTouch"
                  @touchmove="handleOverlayTouch"
                  @touchend="handleOverlayTouch"
                  @touchcancel="handleOverlayTouch"
                ></canvas>
              </div>
            </div>
          </div>
        </oxd-form>
      </div>
      <div
        v-if="contractHintStep"
        class="contract-hint-overlay"
        role="presentation"
      />
      <div
        v-if="contractHintStep"
        class="contract-hint-spotlight"
        :style="contractHintSpotlightStyle"
        aria-hidden="true"
      />
      <div
        v-if="contractHintStep"
        class="contract-hint-arrow"
        :style="contractHintArrowStyle"
        aria-hidden="true"
      />
      <div
        v-if="contractHintStep"
        class="contract-hint-card"
        role="dialog"
        aria-modal="true"
        :style="contractHintCardStyle"
        :aria-label="contractHintTitle"
      >
        <oxd-text tag="h6" class="contract-hint-card__title">
          {{ contractHintTitle }}
        </oxd-text>
        <oxd-text tag="p" class="contract-hint-card__message">
          {{ contractHintMessage }}
        </oxd-text>
        <oxd-button
          display-type="main"
          :label="$t('general.ok')"
          @click="dismissContractHint"
        />
      </div>

      <div v-if="pdf && !isLoading" class="controls-mobile">
        <div ref="pageNavControls" class="controls-mobile-nav">
          <div class="page-text">
            {{ $t('general.page') }}: <span>{{ currentPage }}</span
            >/<span>{{ pageCount }}</span>
          </div>
          <oxd-icon-button
            name="arrow-left"
            class="control-prev"
            @click="prevPage"
          />
          <oxd-icon-button
            name="arrow-right"
            class="control-next"
            @click="nextPage"
          />
        </div>
        <div class="controls-mobile-zoom">
          <oxd-icon-button name="dash" class="control-minus" @click="zoomOut" />
          <oxd-icon-button name="plus" class="control-plus" @click="zoomIn" />
          <oxd-icon-button
            name="arrows-fullscreen"
            class="control-reset"
            :title="$t('general.reset')"
            @click="resetZoom"
          />
        </div>
        <div v-if="isCompactLayout" class="controls-mobile-menu">
          <oxd-icon-button
            name="list"
            class="control-menu"
            @click="toggleSideBar"
          />
        </div>
      </div>
    </div>
    <slot name="footer"></slot>
  </div>
</template>

<script>
import {markRaw, ref, shallowRef} from 'vue';
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';
import {degrees, PDFDocument} from 'pdf-lib';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@/core/util/helper/navigation';
import {OxdIcon, OxdSpinner} from '@ohrm/oxd';
import moment from 'moment';

const signContractModel = {
  signDate: '',
  oldAttachment: null,
  newAttachment: null,
};

/** Hi-DPI multiplier for PDF canvas backing store vs CSS display size */
const PDF_RENDER_QUALITY = 2;

/** Minimum backing-store scale for the draw pad (crisp export onto the PDF overlay) */
const DRAW_SIGNATURE_DPR = 3;

/** CSS stroke width on the draw pad */
const DRAW_SIGNATURE_LINE_WIDTH = 2.5;

/** Size tuning for new signatures (1 = baseline page-relative size) */
const SIGNATURE_SIZE_MULTIPLIER = 1.25;

/** Default width/height as a share of the PDF page on screen (stable across devices) */
const SIGNATURE_PAGE_WIDTH_FRACTION = 0.16 * SIGNATURE_SIZE_MULTIPLIER;
const SIGNATURE_PAGE_MAX_HEIGHT_FRACTION = 0.08 * SIGNATURE_SIZE_MULTIPLIER;

const CONTRACT_HINT_PAGE_NAV = 'pageNav';
const CONTRACT_HINT_DRAW_SIGNATURE = 'drawSignature';
const CONTRACT_HINT_ADD_SIGNATURE = 'addSignature';
const CONTRACT_HINT_SIDEBAR_OPEN_DELAY_MS = 520;

export default {
  name: 'SignContract',
  components: {
    'oxd-icon': OxdIcon,
    'oxd-loading-spinner': OxdSpinner,
  },
  props: {
    empNumber: {
      type: [String, Number],
      required: true,
    },
    hasNewContract: {
      type: Boolean,
      default: false,
    },
    hasUpdatedProfile: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const empNumber = String(props.empNumber);
    const userHasNewContract = ref(!!props.hasNewContract);
    const userHasUpdatedProfile = ref(!!props.hasUpdatedProfile);
    if (!userHasUpdatedProfile.value) {
      navigate('/auth/employeeData');
    } else if (!userHasNewContract.value) {
      navigate('/dashboard/index');
    }

    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${empNumber}/employment-contract`,
    );

    const httpSubmitPDF = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${empNumber}/contract`,
    );
    const orangehrmCardContainer = ref(null);
    const pdfCanvas = ref(null);
    const overlayCanvas = ref(null);
    const drawCanvas = ref(null);
    // PDF.js uses private class fields; must not be wrapped in Vue proxies.
    const pdf = shallowRef(null);
    const pdfBytes = shallowRef(null);
    const currentPage = ref(1);
    const pageCount = ref(1);
    const viewport = ref(null);
    const signatureRenders = ref([]);
    const mode = ref('');
    const typedText = ref('');
    const isDrawing = ref(false);
    const baseScale = ref(1);
    const currentVisualScale = ref(0);
    const zoomStep = ref(0.5);
    const zoomFactor = ref(1);
    const referenceWidth = 1900;
    const panOffset = {x: 0, y: 0};
    const isPanning = ref(false);
    const lastPanPoint = {x: 0, y: 0};
    const isZooming = ref(false);
    const initialDistance = 0;
    const initialZoom = 1;
    const zoomCenter = {x: 0, y: 0};
    const lastTouches = [];

    const getCoordinates = (e, canvas) => {
      const rect = canvas.getBoundingClientRect();

      let clientX;
      let clientY;

      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      // CSS pixel coords — setupCanvas applies ctx.scale(dpr) for HiDPI
      return [clientX - rect.left, clientY - rect.top];
    };
    return {
      http,
      httpSubmitPDF,
      pdfCanvas,
      overlayCanvas,
      drawCanvas,
      pdf,
      pdfBytes,
      currentPage,
      pageCount,
      viewport,
      signatureRenders,
      mode,
      typedText,
      isDrawing,
      baseScale,
      zoomFactor,
      referenceWidth,
      currentVisualScale,
      zoomStep,
      panOffset,
      isPanning,
      lastPanPoint,
      orangehrmCardContainer,
      isZooming,
      initialDistance,
      initialZoom,
      zoomCenter,
      lastTouches,
      getCoordinates,
    };
  },
  data() {
    return {
      isLoading: true,
      contract: {...signContractModel},
      contractFile: null,
      signatureImageFile: null,
      isSidebarOpen: false,
      isMobile: window.innerWidth <= 768,
      isCompactLayout: window.innerWidth <= 1024,
      overlayTouchStart: null,
      overlayTouchHandledAt: 0,
      contractHintStep: null,
      contractHintCardStyle: {},
      contractHintSpotlightStyle: {},
      contractHintArrowStyle: {},
    };
  },

  computed: {
    contractHintTitle() {
      switch (this.contractHintStep) {
        case CONTRACT_HINT_PAGE_NAV:
          return this.$t('auth.contract_page_navigation_hint_title');
        case CONTRACT_HINT_DRAW_SIGNATURE:
          return this.$t('auth.contract_draw_signature_hint_title');
        case CONTRACT_HINT_ADD_SIGNATURE:
          return this.$t('auth.contract_add_signature_hint_title');
        default:
          return '';
      }
    },
    contractHintMessage() {
      switch (this.contractHintStep) {
        case CONTRACT_HINT_PAGE_NAV:
          return this.$t('auth.contract_page_navigation_hint');
        case CONTRACT_HINT_DRAW_SIGNATURE:
          return this.$t('auth.contract_draw_signature_hint');
        case CONTRACT_HINT_ADD_SIGNATURE:
          return this.$t('auth.contract_add_signature_hint');
        default:
          return '';
      }
    },
  },

  mounted() {
    // Add pan event listeners to the canvas container
    this.updateOverlayTouchAction();
    const canvasContainer = this.$refs.pdfCanvas?.parentElement;
    if (canvasContainer) {
      canvasContainer.addEventListener('mousedown', this.startPan);
      if (!this.isTouchDevice()) {
        canvasContainer.addEventListener('touchstart', this.startPan);
      }
    }
    window.addEventListener('resize', this.onViewportResize);
    this.$nextTick(() => {
      this.observePdfViewport();
    });
  },

  beforeMount() {
    this.loadContract();
  },

  beforeUnmount() {
    if (this._viewportResizeTimer) {
      clearTimeout(this._viewportResizeTimer);
    }
    this.pdfViewportObserver?.disconnect();
    window.removeEventListener('resize', this.onViewportResize);
    const canvasContainer = this.$refs.pdfCanvas?.parentElement;
    if (canvasContainer) {
      canvasContainer.removeEventListener('mousedown', this.startPan);
      canvasContainer.removeEventListener('touchstart', this.startPan);
    }

    this.removePanListeners();
  },

  methods: {
    getPdfViewportSize() {
      const viewportEl = this.$refs.pdfViewport;
      if (!viewportEl) {
        return {width: 0, height: 0};
      }

      const rect = viewportEl.getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height,
      };
    },

    // Use 100% width or 100% height of the wrapper — whichever scale is larger.
    getWrapperFitScale(page) {
      const {width: containerWidth, height: containerHeight} =
        this.getPdfViewportSize();
      const pageViewport = page.getViewport({scale: 1});

      if (!containerWidth || !containerHeight) {
        return 1;
      }

      const scaleByWidth = containerWidth / pageViewport.width;
      const scaleByHeight = containerHeight / pageViewport.height;

      return Math.max(scaleByWidth, scaleByHeight);
    },

    observePdfViewport() {
      const viewportEl = this.$refs.pdfViewport;
      if (!viewportEl || typeof ResizeObserver === 'undefined') {
        return;
      }

      this.pdfViewportObserver?.disconnect();
      this.pdfViewportObserver = new ResizeObserver(() => {
        this.onViewportResize();
      });
      this.pdfViewportObserver.observe(viewportEl);
    },

    onViewportResize() {
      this.checkMobile();
      if (this.contractHintStep) {
        this.updateContractHintPosition();
      }
      if (!this.pdf) {
        return;
      }

      if (this._viewportResizeTimer) {
        clearTimeout(this._viewportResizeTimer);
      }

      this._viewportResizeTimer = setTimeout(() => {
        this.renderPage(this.currentPage);
        this.setupCanvas();
      }, 100);
    },

    async zoomIn() {
      this.zoomFactor = Math.min(5, this.zoomFactor + 0.25);
      this.panOffset = {x: 0, y: 0};
      await this.renderPage(this.currentPage);
      this.$nextTick(() => this.resetPdfScroll());
    },

    async zoomOut() {
      this.zoomFactor = Math.max(0.5, this.zoomFactor - 0.25);
      this.panOffset = {x: 0, y: 0};
      await this.renderPage(this.currentPage);
      this.$nextTick(() => this.resetPdfScroll());
    },

    applyZoomTransform() {
      if (this.zoomFactor <= 1) {
        this.panOffset = {x: 0, y: 0};
      }

      const hasPan = this.panOffset.x !== 0 || this.panOffset.y !== 0;
      const transform = hasPan
        ? `translate(${this.panOffset.x}px, ${this.panOffset.y}px)`
        : '';

      const container = this.pdfCanvas?.parentElement;
      if (container) {
        container.style.transform = transform;
        container.style.transformOrigin = 'top left';
      }

      if (this.pdfCanvas) {
        this.pdfCanvas.style.transform = '';
      }
      if (this.overlayCanvas) {
        this.overlayCanvas.style.transform = '';
      }
    },

    async resetZoom() {
      this.zoomFactor = 1;
      this.panOffset = {x: 0, y: 0};
      if (this.pdf) {
        await this.renderPage(this.currentPage);
      } else {
        this.applyZoomTransform();
      }
    },

    // Helper functions for touch gestures
    getTouchDistance(touches) {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    getTouchCenter(touches) {
      if (touches.length < 2) return {x: 0, y: 0};
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
      };
    },

    toggleSideBar() {
      if (this.isCompactLayout) {
        this.isSidebarOpen = !this.isSidebarOpen;
      }
      this.$nextTick(() => {
        this.setupCanvas();
      });
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      this.isCompactLayout = window.innerWidth <= 1024;
    },

    isTouchDevice() {
      return (
        this.isMobile ||
        (typeof window !== 'undefined' &&
          ('ontouchstart' in window || navigator.maxTouchPoints > 0))
      );
    },

    allowPinchZoom() {
      return !this.isTouchDevice();
    },

    updateOverlayTouchAction() {
      const overlay = this.$refs.overlayCanvas;
      if (!overlay) {
        return;
      }

      if (this.isTouchDevice() && this.zoomFactor <= 1) {
        overlay.style.touchAction = 'pan-x pan-y';
      } else {
        overlay.style.touchAction = 'none';
      }
    },
    async loadContract() {
      this.isLoading = true;
      try {
        const response = await this.http.getAll();
        this.updateContractModel(response);
        await this.handleFileUpload(
          `viewAttachment/empNumber/${this.empNumber}/attachId/${this.contract.oldAttachment.id}`,
        );
      } finally {
        this.isLoading = false;
      }
    },
    submitContract() {
      if (this.isLoading) return;
      this.toggleSideBar();
      this.isLoading = true;
      this.httpSubmitPDF
        .request({
          method: 'PUT',
          data: {
            contractAttachment: this.contract.newAttachment,
          },
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .finally(() => {
          this.isLoading = false;
          navigate('/dashboard/index');
        });
    },
    updateContractModel(response) {
      const {data} = response.data;
      this.contract.signDate = '';
      this.contract.oldAttachment = data.contractAttachment?.id
        ? data.contractAttachment
        : null;
      this.contract.newAttachment = null;
    },

    // File or URL upload handler
    async handleFileUpload(e) {
      let arrayBuffer;

      if (typeof e === 'string') {
        const response = await fetch(e);
        if (!response.ok) throw new Error(`${response.statusText}`);
        arrayBuffer = await response.arrayBuffer();
      } else {
        const file = e.target.files[0];
        if (!file) return;
        arrayBuffer = await file.arrayBuffer();
      }

      this.pdfBytes = arrayBuffer;
      // isEvalSupported: false blocks JS execution from crafted PDFs (CVE-2024-4367)
      const pdfDocument = await pdfjsLib.getDocument({
        // getDocument() transfers the buffer to the worker; pass a copy so
        // this.pdfBytes stays usable for pdf-lib signing/downloading.
        data: this.pdfBytes.slice(0),
        isEvalSupported: false,
      }).promise;
      this.pdf = markRaw(pdfDocument);
      this.pageCount = this.pdf.numPages;
      this.maybeShowContractHints();
      if (this.contractHintStep) {
        this.prepareContractHintStep();
      }
      await this.$nextTick();
      await this.renderPage(1);
      this.$nextTick(() => {
        this.setupCanvas();
        this.observePdfViewport();
      });
    },

    getContractHintSteps() {
      const steps = [CONTRACT_HINT_DRAW_SIGNATURE, CONTRACT_HINT_ADD_SIGNATURE];
      if (this.pageCount > 1) {
        steps.unshift(CONTRACT_HINT_PAGE_NAV);
      }
      return steps;
    },

    maybeShowContractHints() {
      const steps = this.getContractHintSteps();
      this.contractHintStep = steps[0] ?? null;
    },

    hintTargetUsesSidebar() {
      return (
        this.contractHintStep === CONTRACT_HINT_DRAW_SIGNATURE ||
        this.contractHintStep === CONTRACT_HINT_ADD_SIGNATURE
      );
    },

    getContractHintTargetElement() {
      switch (this.contractHintStep) {
        case CONTRACT_HINT_PAGE_NAV:
          return this.$refs.pageNavControls;
        case CONTRACT_HINT_DRAW_SIGNATURE:
          return this.$refs.drawCanvas;
        case CONTRACT_HINT_ADD_SIGNATURE: {
          const button = this.$refs.addSignatureButton;
          return button?.$el ?? button;
        }
        default:
          return null;
      }
    },

    prepareContractHintStep() {
      if (!this.contractHintStep) {
        this.clearContractHintStyles();
        return;
      }

      if (this.hintTargetUsesSidebar() && this.isCompactLayout) {
        this.isSidebarOpen = true;
      }

      const measureHint = () => {
        if (this.contractHintStep === CONTRACT_HINT_DRAW_SIGNATURE) {
          this.setupCanvas();
        }
        this.updateContractHintPosition();
      };

      const sidebarDelay =
        this.hintTargetUsesSidebar() && this.isCompactLayout
          ? CONTRACT_HINT_SIDEBAR_OPEN_DELAY_MS
          : 0;

      this.$nextTick(() => {
        setTimeout(measureHint, sidebarDelay);
      });
    },

    updateContractHintPosition() {
      const target = this.getContractHintTargetElement();
      if (!target) {
        return;
      }

      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return;
      }

      const cardMaxWidth = Math.min(352, window.innerWidth - 32);
      const targetCenterX = rect.left + rect.width / 2;
      const minLeft = 16 + cardMaxWidth / 2;
      const maxLeft = window.innerWidth - 16 - cardMaxWidth / 2;
      const left = Math.min(Math.max(targetCenterX, minLeft), maxLeft);
      const arrowHeight = 16;
      const arrowGap = 8;
      const anchorTop = rect.top - arrowHeight - arrowGap;

      this.contractHintSpotlightStyle = {
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        background: this.hintTargetUsesSidebar() ? '#222831' : '#010423',
      };
      this.contractHintArrowStyle = {
        left: `${targetCenterX}px`,
        top: `${anchorTop}px`,
        '--arrow-height': `${arrowHeight}px`,
      };
      this.contractHintCardStyle = {
        left: `${left}px`,
        top: `${anchorTop}px`,
        width: `${cardMaxWidth}px`,
      };
    },

    clearContractHintStyles() {
      this.contractHintCardStyle = {};
      this.contractHintSpotlightStyle = {};
      this.contractHintArrowStyle = {};
    },

    dismissContractHint() {
      const steps = this.getContractHintSteps();
      const currentIndex = steps.indexOf(this.contractHintStep);

      if (currentIndex >= 0 && currentIndex < steps.length - 1) {
        this.contractHintStep = steps[currentIndex + 1];
        this.prepareContractHintStep();
        return;
      }

      this.contractHintStep = null;
      this.clearContractHintStyles();
    },

    resetPdfScroll() {
      const viewportEl = this.$refs.pdfViewport;
      if (viewportEl) {
        viewportEl.scrollTop = 0;
        viewportEl.scrollLeft = 0;
      }
    },

    // Page navigation
    async prevPage() {
      if (this.currentPage > 1) {
        await this.renderPage(this.currentPage - 1);
        this.$nextTick(() => this.resetPdfScroll());
      }
    },

    async nextPage() {
      if (this.currentPage < this.pdf.numPages) {
        await this.renderPage(this.currentPage + 1);
        this.$nextTick(() => this.resetPdfScroll());
      }
    },

    // Render PDF page
    async renderPage(num) {
      this.preserveDraggableWrapperNormalizedPositions();

      this.currentPage = num;

      const page = await this.pdf.getPage(num);
      let displayScale = this.getWrapperFitScale(page) * this.zoomFactor;

      const {width: viewportWidth, height: viewportHeight} =
        this.getPdfViewportSize();
      if (!viewportWidth || !viewportHeight) {
        await this.$nextTick();
        displayScale = this.getWrapperFitScale(page) * this.zoomFactor;
      }

      const renderScale = displayScale * PDF_RENDER_QUALITY;

      this.viewport = page.getViewport({scale: renderScale});

      const canvas = this.pdfCanvas;
      const overlay = this.overlayCanvas;

      // For high res canvas
      canvas.width = this.viewport.width;
      canvas.height = this.viewport.height;
      overlay.width = this.viewport.width;
      overlay.height = this.viewport.height;

      const displayViewport = page.getViewport({scale: displayScale});
      const displayWidth = `${displayViewport.width}px`;
      const displayHeight = `${displayViewport.height}px`;

      canvas.style.width = displayWidth;
      canvas.style.height = displayHeight;
      overlay.style.width = displayWidth;
      overlay.style.height = displayHeight;

      const canvasContainer = canvas.parentElement;
      const scrollSurface = this.$refs.pdfScrollSurface;
      if (canvasContainer) {
        canvasContainer.style.width = displayWidth;
        canvasContainer.style.height = displayHeight;
        canvasContainer.style.minWidth = displayWidth;
        canvasContainer.style.minHeight = displayHeight;
      }
      if (scrollSurface) {
        scrollSurface.style.width = displayWidth;
        scrollSurface.style.height = displayHeight;
        scrollSurface.style.minWidth = displayWidth;
        scrollSurface.style.minHeight = displayHeight;
      }

      await page.render({
        canvas,
        canvasContext: canvas.getContext('2d'),
        viewport: this.viewport,
      }).promise;

      this.renderOverlay();
      this.syncDraggableWrappersToDisplaySize();
      this.applyZoomTransform();
      this.updateOverlayTouchAction();
    },

    // Render overlay with signatures
    renderOverlay() {
      const ctx = this.overlayCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);

      this.signatureRenders
        .filter((s) => s.page === this.currentPage)
        .forEach((s) => {
          const rect = this.getSignatureCanvasRect(s);
          if (!rect) {
            return;
          }
          const {x, y, width, height} = rect;
          ctx.save();
          if (s.rotation) {
            ctx.translate(x + width / 2, y + height / 2);
            ctx.rotate(s.rotation);
            ctx.drawImage(s.img, -width / 2, -height / 2, width, height);
          } else {
            ctx.drawImage(s.img, x, y, width, height);
          }
          ctx.restore();
        });
    },

    getOverlayDisplaySize() {
      const overlay = this.overlayCanvas;
      if (!overlay) {
        return {width: 0, height: 0};
      }
      const width =
        parseFloat(overlay.style.width) ||
        overlay.getBoundingClientRect().width ||
        0;
      const height =
        parseFloat(overlay.style.height) ||
        overlay.getBoundingClientRect().height ||
        0;
      return {width, height};
    },

    /** Backing-store pixels per CSS pixel on the PDF overlay (matches PDF_RENDER_QUALITY). */
    getSignatureOverlayPixelRatio() {
      const {width} = this.getOverlayDisplaySize();
      const overlay = this.overlayCanvas;
      if (overlay?.width && width > 0) {
        return overlay.width / width;
      }
      return PDF_RENDER_QUALITY;
    },

    /** Signatures are stored as fractions of the visible PDF page (0–1). */
    getSignatureCanvasRect(sig) {
      const overlay = this.overlayCanvas;
      if (!overlay?.width || !overlay?.height) {
        return null;
      }
      return {
        x: sig.x * overlay.width,
        y: sig.y * overlay.height,
        width: sig.width * overlay.width,
        height: sig.height * overlay.height,
      };
    },

    displayCoordsToNormalized(displayX, displayY, displayW, displayH) {
      const {width, height} = this.getOverlayDisplaySize();
      if (!width || !height) {
        return {x: 0, y: 0, width: 0, height: 0};
      }
      return {
        x: displayX / width,
        y: displayY / height,
        width: displayW / width,
        height: displayH / height,
      };
    },

    normalizedToDisplayCoords(sig) {
      const {width, height} = this.getOverlayDisplaySize();
      return {
        left: sig.x * width,
        top: sig.y * height,
        width: sig.width * width,
        height: sig.height * height,
      };
    },

    /**
     * Display size (CSS px) from aspect ratio, scaled to a fixed share of the PDF page.
     * Keeps signatures the same relative size on phone, tablet, and desktop.
     */
    getSignatureDisplaySize(naturalWidth, naturalHeight) {
      const w = Math.max(1, naturalWidth);
      const h = Math.max(1, naturalHeight);
      const aspect = w / h;
      const {width: pageW, height: pageH} = this.getOverlayDisplaySize();

      if (!pageW || !pageH) {
        const fallbackWidth = 100 * SIGNATURE_SIZE_MULTIPLIER;
        return {
          width: fallbackWidth,
          height: fallbackWidth / aspect,
        };
      }

      let displayWidth = pageW * SIGNATURE_PAGE_WIDTH_FRACTION;
      let displayHeight = displayWidth / aspect;
      const maxHeight = pageH * SIGNATURE_PAGE_MAX_HEIGHT_FRACTION;

      if (displayHeight > maxHeight) {
        displayHeight = maxHeight;
        displayWidth = displayHeight * aspect;
      }

      const maxWidth = pageW * 0.45;
      if (displayWidth > maxWidth) {
        displayWidth = maxWidth;
        displayHeight = displayWidth / aspect;
      }

      return {width: displayWidth, height: displayHeight};
    },

    updateWrapperNormalizedDataset(wrapper) {
      const {width, height} = this.getOverlayDisplaySize();
      if (!width || !height) {
        return;
      }

      wrapper.dataset.normX = String(
        (parseFloat(wrapper.style.left) || 0) / width,
      );
      wrapper.dataset.normY = String(
        (parseFloat(wrapper.style.top) || 0) / height,
      );
      wrapper.dataset.normW = String(
        (parseFloat(wrapper.style.width) || 100) / width,
      );
      wrapper.dataset.normH = String(
        (parseFloat(wrapper.style.height) || 50) / height,
      );
    },

    applyNormalizedFromDataset(wrapper) {
      const {width, height} = this.getOverlayDisplaySize();
      if (!width || !height || wrapper.dataset.normX === undefined) {
        return;
      }

      wrapper.style.left = `${parseFloat(wrapper.dataset.normX) * width}px`;
      wrapper.style.top = `${parseFloat(wrapper.dataset.normY) * height}px`;
      wrapper.style.width = `${parseFloat(wrapper.dataset.normW) * width}px`;
      wrapper.style.height = `${parseFloat(wrapper.dataset.normH) * height}px`;
    },

    preserveDraggableWrapperNormalizedPositions() {
      const container = this.pdfCanvas?.parentElement;
      if (!container) {
        return;
      }

      container.querySelectorAll('.draggable-wrapper').forEach((wrapper) => {
        this.updateWrapperNormalizedDataset(wrapper);
      });
    },

    syncDraggableWrappersToDisplaySize() {
      const container = this.pdfCanvas?.parentElement;
      if (!container) {
        return;
      }

      container.querySelectorAll('.draggable-wrapper').forEach((wrapper) => {
        this.applyNormalizedFromDataset(wrapper);
      });
    },

    /** Place a new signature in the center of the currently visible PDF area. */
    getSignaturePlacementCoords(sigWidth, sigHeight) {
      const viewportEl = this.$refs.pdfViewport;
      const container = this.pdfCanvas?.parentElement;
      if (!viewportEl || !container) {
        return {left: 10, top: 10};
      }

      const viewportRect = viewportEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const visibleLeft = Math.max(viewportRect.left, containerRect.left);
      const visibleTop = Math.max(viewportRect.top, containerRect.top);
      const visibleRight = Math.min(viewportRect.right, containerRect.right);
      const visibleBottom = Math.min(viewportRect.bottom, containerRect.bottom);

      const visibleWidth = Math.max(0, visibleRight - visibleLeft);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      const centerClientX = visibleLeft + visibleWidth / 2;
      const centerClientY = visibleTop + visibleHeight / 2;

      let left = centerClientX - containerRect.left - sigWidth / 2;
      let top = centerClientY - containerRect.top - sigHeight / 2;

      const {width: canvasW, height: canvasH} = this.getOverlayDisplaySize();
      if (canvasW && canvasH) {
        left = Math.max(0, Math.min(left, canvasW - sigWidth));
        top = Math.max(0, Math.min(top, canvasH - sigHeight));
      }

      return {left, top};
    },

    appendSignatureWrapper(imgSrc, options = {}) {
      const size =
        options.width != null && options.height != null
          ? {width: options.width, height: options.height}
          : this.getSignatureDisplaySize(2, 1);
      const {width, height} = size;
      const {left, top} = this.getSignaturePlacementCoords(width, height);
      const wrapper = this.createDraggableWrapper(imgSrc, {
        ...options,
        left,
        top,
        width,
        height,
      });
      this.pdfCanvas.parentElement.appendChild(wrapper);
      return wrapper;
    },

    startPan(e) {
      if (this.zoomFactor <= 1 || this.isZooming) return;

      e.preventDefault();
      this.isPanning = true;

      // Get initial coordinates
      let clientX, clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      this.lastPanPoint = {x: clientX, y: clientY};

      document.body.style.cursor = 'grabbing';

      // Add event listeners
      document.addEventListener('mousemove', this.handlePan);
      document.addEventListener('mouseup', this.stopPan);
      document.addEventListener('touchmove', this.handlePan, {passive: false});
      document.addEventListener('touchend', this.stopPan);
      document.addEventListener('touchcancel', this.stopPan);
    },

    handlePan(e) {
      if (!this.isPanning || this.isZooming) return;
      e.preventDefault();

      let clientX = 0,
        clientY = 0;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX || 0;
        clientY = e.touches[0].clientY || 0;
      } else {
        clientX = e.clientX || 0;
        clientY = e.clientY || 0;
      }

      if (
        !this.lastPanPoint ||
        typeof this.lastPanPoint.x !== 'number' ||
        typeof this.lastPanPoint.y !== 'number'
      ) {
        this.lastPanPoint = {x: clientX, y: clientY};
        return;
      }

      const deltaX = clientX - this.lastPanPoint.x;
      const deltaY = clientY - this.lastPanPoint.y;

      if (isNaN(deltaX) || isNaN(deltaY)) {
        return;
      }

      if (
        !this.panOffset ||
        typeof this.panOffset.x !== 'number' ||
        typeof this.panOffset.y !== 'number'
      ) {
        this.panOffset = {x: 0, y: 0};
      }

      const zoomFactor =
        this.zoomFactor && this.zoomFactor > 0 ? this.zoomFactor : 1;

      this.panOffset.x += deltaX / zoomFactor;
      this.panOffset.y += deltaY / zoomFactor;
      this.applyZoomTransform();
      this.lastPanPoint = {x: clientX, y: clientY};
    },

    removePanListeners() {
      document.removeEventListener('mousemove', this.handlePan);
      document.removeEventListener('mouseup', this.stopPan);
      document.removeEventListener('touchmove', this.handlePan);
      document.removeEventListener('touchend', this.stopPan);
      document.removeEventListener('touchcancel', this.stopPan);
    },

    stopPan(e) {
      if (e) e.preventDefault();

      const wasPanning = this.isPanning;
      this.isPanning = false;
      document.body.style.cursor = 'default';
      this.removePanListeners();

      if (wasPanning && this.zoomFactor <= 1) {
        this.panOffset = {x: 0, y: 0};
        this.applyZoomTransform();
      }
    },

    resetAllGestures() {
      this.isPanning = false;
      this.isZooming = false;
      this.initialDistance = 0;
      this.initialZoom = 1;
      this.zoomCenter = {x: 0, y: 0};
      this.lastPanPoint = {x: 0, y: 0};
      document.body.style.cursor = 'default';

      this.removePanListeners();
    },

    startPinchZoom(e) {
      if (!this.allowPinchZoom() || e.touches.length < 2) {
        return;
      }

      // Complete reset before starting
      this.resetAllGestures();

      this.isZooming = true;
      const touches = Array.from(e.touches);
      this.initialDistance = this.getTouchDistance(touches);
      this.initialZoom = this.zoomFactor;

      // Get zoom center relative to the canvas
      const rect = this.overlayCanvas.getBoundingClientRect();
      const center = this.getTouchCenter(touches);
      this.zoomCenter = {
        x: center.x - rect.left,
        y: center.y - rect.top,
      };
    },

    handlePinchZoom(e) {
      if (!this.isZooming || e.touches.length < 2) return;

      const touches = Array.from(e.touches);
      const currentDistance = this.getTouchDistance(touches);
      if (this.initialDistance === 0) return;

      const scaleFactor = currentDistance / this.initialDistance;
      let newZoom = this.initialZoom * scaleFactor;
      newZoom = Math.max(0.5, Math.min(5, newZoom));

      this.zoomFactor = newZoom;
      this.panOffset = {x: 0, y: 0};

      if (!this._pinchRenderScheduled) {
        this._pinchRenderScheduled = true;
        requestAnimationFrame(() => {
          this.renderPage(this.currentPage).finally(() => {
            this._pinchRenderScheduled = false;
          });
        });
      }
    },

    stopPinchZoom() {
      const wasZooming = this.isZooming;
      this.isZooming = false;
      this.initialDistance = 0;
      this.initialZoom = 1;
      this.zoomCenter = {x: 0, y: 0};
      this.panOffset = {x: 0, y: 0};

      if (wasZooming && this.pdf) {
        this.renderPage(this.currentPage);
      } else {
        this.applyZoomTransform();
      }
    },

    handleOverlayTouch(e) {
      const touches = Array.from(e.touches || []);
      const pinchAllowed = this.allowPinchZoom();

      if (e.type === 'touchstart') {
        if (touches.length === 1 && this.zoomFactor <= 1) {
          this.overlayTouchStart = {
            x: touches[0].clientX,
            y: touches[0].clientY,
          };
          return;
        }
        this.overlayTouchStart = null;
      }

      if (e.type === 'touchmove' && !this.isPanning && !this.isZooming) {
        return;
      }

      const isActiveGesture =
        this.isPanning ||
        this.isZooming ||
        (e.type === 'touchstart' &&
          touches.length === 1 &&
          this.zoomFactor > 1) ||
        (pinchAllowed &&
          touches.length >= 2 &&
          (e.type === 'touchstart' || e.type === 'touchmove'));

      if (isActiveGesture) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (e.type === 'touchstart') {
        if (touches.length === 1) {
          if (this.zoomFactor > 1) {
            this.startPan(e);
          }
        } else if (touches.length === 2 && pinchAllowed) {
          if (this.isPanning) {
            this.stopPan();
          }
          this.startPinchZoom(e);
        }
      } else if (e.type === 'touchmove') {
        if (touches.length === 1) {
          if (this.isPanning && !this.isZooming) {
            this.handlePan(e);
          }
        } else if (touches.length === 2 && pinchAllowed) {
          if (this.isZooming) {
            this.handlePinchZoom(e);
          } else if (this.isPanning) {
            this.stopPan();
            this.startPinchZoom(e);
          }
        }
      } else if (e.type === 'touchend' || e.type === 'touchcancel') {
        const changedTouches = Array.from(e.changedTouches || []);

        if (touches.length === 0) {
          const wasZooming = this.isZooming;
          const wasPanning = this.isPanning;

          if (wasPanning) {
            this.stopPan(e);
          }
          if (wasZooming) {
            this.stopPinchZoom();
          }

          if (!wasZooming && !wasPanning && changedTouches.length === 1) {
            this.handleOverlayTapSelect(e, changedTouches[0]);
          }
        } else if (touches.length === 1 && this.isZooming) {
          this.stopPinchZoom();
        }
      }
    },

    handleOverlayTapSelect(e, touch) {
      if (this.overlayTouchStart && touch) {
        const dx = touch.clientX - this.overlayTouchStart.x;
        const dy = touch.clientY - this.overlayTouchStart.y;
        if (Math.hypot(dx, dy) > 12) {
          this.overlayTouchStart = null;
          return;
        }
      }
      this.overlayTouchStart = null;
      this.overlayTouchHandledAt = Date.now();
      this.handleOverlayClick(e, touch);
    },

    handleOverlayClick(e, touchOverride = null) {
      if (this.isPanning || this.isZooming) return;
      if (
        e.type === 'click' &&
        this.overlayTouchHandledAt &&
        Date.now() - this.overlayTouchHandledAt < 400
      ) {
        return;
      }
      if (e.preventDefault) e.preventDefault();
      const rect = this.overlayCanvas.getBoundingClientRect();

      let clientX, clientY;
      if (touchOverride) {
        clientX = touchOverride.clientX;
        clientY = touchOverride.clientY;
      } else if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if (e.changedTouches && e.changedTouches.length > 0) {
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      // Get display coordinates
      const displayClickX = clientX - rect.left;
      const displayClickY = clientY - rect.top;

      const {width: displayW, height: displayH} = this.getOverlayDisplaySize();
      if (!displayW || !displayH) {
        return;
      }
      const clickX = displayClickX / displayW;
      const clickY = displayClickY / displayH;

      const clickedSigs = this.signatureRenders.filter(
        (s) => s.page === this.currentPage,
      );

      for (let i = clickedSigs.length - 1; i >= 0; i--) {
        const sig = clickedSigs[i];
        if (
          clickX >= sig.x &&
          clickX <= sig.x + sig.width &&
          clickY >= sig.y &&
          clickY <= sig.y + sig.height
        ) {
          this.makeSignatureEditable(sig);
          break;
        }
      }
    },

    // Make signature editable
    makeSignatureEditable(signature) {
      const index = this.signatureRenders.indexOf(signature);
      if (index > -1) {
        this.signatureRenders.splice(index, 1);
      }

      const display = this.normalizedToDisplayCoords(signature);

      const wrapper = this.createDraggableWrapper(signature.img.src, {
        left: display.left,
        top: display.top,
        width: display.width,
        height: display.height,
        rotation: signature.rotation || 0,
      });

      wrapper.dataset.normX = String(signature.x);
      wrapper.dataset.normY = String(signature.y);
      wrapper.dataset.normW = String(signature.width);
      wrapper.dataset.normH = String(signature.height);

      this.pdfCanvas.parentElement.appendChild(wrapper);
      this.renderOverlay();
    },

    // Wrapper for sign that created
    createDraggableWrapper(imgSrc, options = {}) {
      const wrapper = document.createElement('div');
      wrapper.className = 'draggable-wrapper';
      wrapper.style.left = (options.left || 10) + 'px';
      wrapper.style.top = (options.top || 10) + 'px';
      const defaultSize = this.getSignatureDisplaySize(2, 1);
      wrapper.style.width = (options.width || defaultSize.width) + 'px';
      wrapper.style.height = (options.height || defaultSize.height) + 'px';

      const el = document.createElement('img');
      el.src = imgSrc;

      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.textContent = '×';
      deleteBtn.className = 'delete-btn';
      deleteBtn.setAttribute('aria-label', this.$t('general.delete'));

      const rotateBtn = document.createElement('div');
      rotateBtn.textContent = '⤾';
      rotateBtn.className = 'rotate-btn';

      wrapper.appendChild(el);
      wrapper.appendChild(deleteBtn);
      wrapper.appendChild(rotateBtn);

      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        wrapper.remove();
        this.renderOverlay();
      });

      // Add resize handles
      this.createResizeHandles(wrapper);
      this.addResizeEventListeners(wrapper);

      let rotation = options.rotation || 0;
      if (rotation) {
        wrapper.style.transform = `rotate(${rotation}rad)`;
      }

      let isRotating = false;
      let startAngle = 0;
      let centerX = 0;
      let centerY = 0;

      // Mouse and Touch Events rotation
      const startRotate = (e) => {
        e.preventDefault();
        e.stopPropagation();

        isRotating = true;

        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
          clientY = e.touches[0].clientY;
        } else {
          clientX = e.clientX;
          clientY = e.clientY;
        }

        const rect = wrapper.getBoundingClientRect();
        centerX = rect.left + rect.width / 2;
        centerY = rect.top + rect.height / 2;

        startAngle =
          Math.atan2(clientY - centerY, clientX - centerX) - rotation;
        document.body.style.cursor = 'grab';

        const handleRotate = (e) => {
          if (!isRotating) return;
          e.preventDefault();

          let clientX, clientY;
          if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
          } else {
            clientX = e.clientX;
            clientY = e.clientY;
          }

          const currentAngle = Math.atan2(clientY - centerY, clientX - centerX);
          rotation = currentAngle - startAngle;
          wrapper.style.transform = `rotate(${rotation}rad)`;
        };

        const stopRotate = (e) => {
          if (e) e.preventDefault();
          isRotating = false;
          document.body.style.cursor = 'default';

          document.removeEventListener('mousemove', handleRotate);
          document.removeEventListener('mouseup', stopRotate);
          document.removeEventListener('touchmove', handleRotate);
          document.removeEventListener('touchend', stopRotate);
          document.removeEventListener('touchcancel', stopRotate);
        };

        document.addEventListener('mousemove', handleRotate);
        document.addEventListener('mouseup', stopRotate);
        document.addEventListener('touchmove', handleRotate, {passive: false});
        document.addEventListener('touchend', stopRotate);
        document.addEventListener('touchcancel', stopRotate);
      };

      rotateBtn.addEventListener('mousedown', startRotate);
      rotateBtn.addEventListener('touchstart', startRotate);

      rotateBtn.addEventListener('mouseenter', () => {
        rotateBtn.style.background = '#ddd';
        rotateBtn.style.transform = 'scale(1.1)';
      });

      rotateBtn.addEventListener('mouseleave', () => {
        if (!isRotating) {
          rotateBtn.style.background = '#eee';
          rotateBtn.style.transform = 'scale(1)';
        }
      });

      // Mouse and Touch Events Drag
      const startDrag = (e) => {
        if (
          e.target === rotateBtn ||
          e.target === deleteBtn ||
          e.target.classList.contains('resize-handle')
        ) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
          clientY = e.touches[0].clientY;
        } else {
          clientX = e.clientX;
          clientY = e.clientY;
        }

        const rect = wrapper.getBoundingClientRect();
        const parentRect = wrapper.parentElement.getBoundingClientRect();
        const offsetX = clientX - rect.left;
        const offsetY = clientY - rect.top;
        let didMove = false;

        const dragMove = (e) => {
          e.preventDefault();

          let clientX, clientY;
          if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
          } else {
            clientX = e.clientX;
            clientY = e.clientY;
          }

          const left = clientX - parentRect.left - offsetX;
          const top = clientY - parentRect.top - offsetY;
          wrapper.style.left = `${left}px`;
          wrapper.style.top = `${top}px`;
          didMove = true;
          this.updateWrapperNormalizedDataset(wrapper);
        };

        const dragEnd = (e) => {
          if (e) e.preventDefault();

          document.removeEventListener('mousemove', dragMove);
          document.removeEventListener('mouseup', dragEnd);
          document.removeEventListener('touchmove', dragMove);
          document.removeEventListener('touchend', dragEnd);
          document.removeEventListener('touchcancel', dragEnd);

          if (!didMove) {
            return;
          }

          const imgObj = new Image();
          imgObj.onload = () => {
            // Convert display coordinates back to canvas coordinates for storage
            const displayX = parseFloat(wrapper.style.left) || 0;
            const displayY = parseFloat(wrapper.style.top) || 0;
            const displayWidth = parseFloat(wrapper.style.width) || 100;
            const displayHeight = parseFloat(wrapper.style.height) || 50;

            const normalized = this.displayCoordsToNormalized(
              displayX,
              displayY,
              displayWidth,
              displayHeight,
            );

            this.signatureRenders.push({
              page: this.currentPage,
              x: normalized.x,
              y: normalized.y,
              width: normalized.width,
              height: normalized.height,
              img: imgObj,
              rotation,
            });
            wrapper.remove();
            this.renderOverlay();
          };
          imgObj.src = el.src;
        };

        document.addEventListener('mousemove', dragMove);
        document.addEventListener('mouseup', dragEnd);
        document.addEventListener('touchmove', dragMove, {passive: false});
        document.addEventListener('touchend', dragEnd);
        document.addEventListener('touchcancel', dragEnd);
      };

      wrapper.addEventListener('mousedown', startDrag);
      wrapper.addEventListener('touchstart', startDrag);

      this.updateWrapperNormalizedDataset(wrapper);

      return wrapper;
    },

    // Create resize handles for sign image
    createResizeHandles(wrapper) {
      const handles = ['nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e'];
      handles.forEach((position) => {
        const handle = document.createElement('div');
        handle.className = `resize-handle ${position}`;
        handle.dataset.position = position;
        wrapper.appendChild(handle);
      });
    },

    // Add resize event listeners
    addResizeEventListeners(wrapper) {
      const handles = wrapper.querySelectorAll('.resize-handle');

      handles.forEach((handle) => {
        const startResize = (e) => {
          e.preventDefault();
          e.stopPropagation();

          let startX, startY;
          if (e.touches && e.touches.length > 0) {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
          } else {
            startX = e.clientX;
            startY = e.clientY;
          }

          const position = handle.dataset.position;
          const startWidth = parseFloat(wrapper.style.width);
          const startHeight = parseFloat(wrapper.style.height);
          const startLeft = parseFloat(wrapper.style.left);
          const startTop = parseFloat(wrapper.style.top);

          const handleResize = (e) => {
            e.preventDefault();

            let clientX, clientY;
            if (e.touches && e.touches.length > 0) {
              clientX = e.touches[0].clientX;
              clientY = e.touches[0].clientY;
            } else {
              clientX = e.clientX;
              clientY = e.clientY;
            }

            const deltaX = clientX - startX;
            const deltaY = clientY - startY;

            let newWidth = startWidth;
            let newHeight = startHeight;
            let newLeft = startLeft;
            let newTop = startTop;

            if (position.includes('e')) {
              newWidth = Math.max(20, startWidth + deltaX);
            } else if (position.includes('w')) {
              newWidth = Math.max(20, startWidth - deltaX);
              newLeft = startLeft + (startWidth - newWidth);
            }

            if (position.includes('s')) {
              newHeight = Math.max(20, startHeight + deltaY);
            } else if (position.includes('n')) {
              newHeight = Math.max(20, startHeight - deltaY);
              newTop = startTop + (startHeight - newHeight);
            }

            if (position === 'e' || position === 'w') {
              newHeight = startHeight;
              newTop = startTop;
            }

            if (position === 'n' || position === 's') {
              newWidth = startWidth;
              newLeft = startLeft;
            }

            wrapper.style.width = `${newWidth}px`;
            wrapper.style.height = `${newHeight}px`;
            wrapper.style.left = `${newLeft}px`;
            wrapper.style.top = `${newTop}px`;
          };

          const stopResize = (e) => {
            if (e) e.preventDefault();

            document.removeEventListener('mousemove', handleResize);
            document.removeEventListener('mouseup', stopResize);
            document.removeEventListener('touchmove', handleResize);
            document.removeEventListener('touchend', stopResize);
            document.removeEventListener('touchcancel', stopResize);

            this.updateWrapperNormalizedDataset(wrapper);
          };

          document.addEventListener('mousemove', handleResize);
          document.addEventListener('mouseup', stopResize);
          document.addEventListener('touchmove', handleResize, {
            passive: false,
          });
          document.addEventListener('touchend', stopResize);
          document.addEventListener('touchcancel', stopResize);
        };

        handle.addEventListener('mousedown', startResize);
        handle.addEventListener('touchstart', startResize);
      });
    },

    getDrawSignatureDpr() {
      return Math.max(window.devicePixelRatio || 1, DRAW_SIGNATURE_DPR);
    },

    /**
     * Bounding box of dark ink on the draw pad (device pixels).
     * @return {{x:number,y:number,width:number,height:number}|null}
     */
    getInkBoundingBox(imageData, canvasWidth, canvasHeight) {
      const {data} = imageData;
      let minX = canvasWidth;
      let minY = canvasHeight;
      let maxX = 0;
      let maxY = 0;
      let found = false;

      for (let y = 0; y < canvasHeight; y++) {
        for (let x = 0; x < canvasWidth; x++) {
          const i = (y * canvasWidth + x) * 4;
          const alpha = data[i + 3];
          if (alpha < 16) {
            continue;
          }
          const red = data[i];
          const green = data[i + 1];
          const blue = data[i + 2];
          if (red > 235 && green > 235 && blue > 235) {
            continue;
          }
          found = true;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }

      if (!found) {
        return null;
      }

      const pad = Math.ceil(this.getDrawSignatureDpr());
      const x = Math.max(0, minX - pad);
      const y = Math.max(0, minY - pad);
      const right = Math.min(canvasWidth, maxX + pad + 1);
      const bottom = Math.min(canvasHeight, maxY + pad + 1);

      return {
        x,
        y,
        width: Math.max(1, right - x),
        height: Math.max(1, bottom - y),
      };
    },

    /** Black ink, transparent background for PDF placement. */
    makeSignatureInkTransparent(imageData) {
      const {data} = imageData;
      for (let i = 0; i < data.length; i += 4) {
        const red = data[i];
        const green = data[i + 1];
        const blue = data[i + 2];
        const alpha = data[i + 3];
        if (alpha < 16 || (red > 235 && green > 235 && blue > 235)) {
          data[i + 3] = 0;
          continue;
        }
        data[i] = 0;
        data[i + 1] = 0;
        data[i + 2] = 0;
      }
      return imageData;
    },

    // Drawing functionality
    setupCanvas() {
      const canvas = this.$refs.drawCanvas;
      if (!canvas) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return;
      }

      const dpr = this.getDrawSignatureDpr();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    },

    startDrawing(e) {
      e.preventDefault();
      this.isDrawing = true;
      const ctx = this.drawCanvas.getContext('2d');
      const [x, y] = this.getCoordinates(e, this.drawCanvas);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = DRAW_SIGNATURE_LINE_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
    },

    draw(e) {
      if (!this.isDrawing) return;
      e.preventDefault();
      const ctx = this.drawCanvas.getContext('2d');
      const [x, y] = this.getCoordinates(e, this.drawCanvas);
      ctx.lineTo(x, y);
      ctx.stroke();
    },

    stopDrawing(e) {
      if (e) e.preventDefault();
      this.isDrawing = false;
    },

    clearCanvas() {
      this.setupCanvas();
    },

    // Add drawn signature
    async addDrawnSignature() {
      if (this.isLoading) return;

      const canvas = this.drawCanvas;
      const ctx = canvas?.getContext('2d');
      if (!ctx || !canvas?.width || !canvas?.height) {
        return;
      }

      // Read ink before toggleSideBar() — its nextTick runs setupCanvas() and clears the pad
      const fullImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const bounds = this.getInkBoundingBox(
        fullImageData,
        canvas.width,
        canvas.height,
      );
      if (!bounds) {
        return;
      }

      const cropped = document.createElement('canvas');
      cropped.width = bounds.width;
      cropped.height = bounds.height;
      const croppedCtx = cropped.getContext('2d');
      const croppedImageData = ctx.getImageData(
        bounds.x,
        bounds.y,
        bounds.width,
        bounds.height,
      );
      croppedCtx.putImageData(
        this.makeSignatureInkTransparent(croppedImageData),
        0,
        0,
      );

      const dpr = this.getDrawSignatureDpr();
      const inkCssWidth = bounds.width / dpr;
      const inkCssHeight = bounds.height / dpr;

      await this.resetZoom();
      this.toggleSideBar();
      await this.$nextTick();

      const {width: displayWidth, height: displayHeight} =
        this.getSignatureDisplaySize(inkCssWidth, inkCssHeight);
      const pixelRatio = this.getSignatureOverlayPixelRatio();
      const exportWidth = Math.max(1, Math.round(displayWidth * pixelRatio));
      const exportHeight = Math.max(1, Math.round(displayHeight * pixelRatio));

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = exportWidth;
      exportCanvas.height = exportHeight;
      const exportCtx = exportCanvas.getContext('2d');
      exportCtx.clearRect(0, 0, exportWidth, exportHeight);
      exportCtx.imageSmoothingEnabled = true;
      exportCtx.imageSmoothingQuality = 'high';
      exportCtx.drawImage(cropped, 0, 0, exportWidth, exportHeight);

      const container = this.pdfCanvas?.parentElement;
      if (!container) {
        return;
      }

      this.appendSignatureWrapper(exportCanvas.toDataURL('image/png'), {
        width: displayWidth,
        height: displayHeight,
      });
    },

    // Add typed signature, not used rn
    addTypedSignature() {
      const text = this.typedText.trim();
      if (!text) {
        alert('Enter text');
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 300;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.font = '48px cursive';
      ctx.fillText(text, 10, 60);
      ctx.strokeStyle = '#000000';
      const img = new Image();
      img.src = canvas.toDataURL();
      img.onload = () => {
        this.$nextTick(() => {
          const {width, height} = this.getSignatureDisplaySize(300, 100);
          this.appendSignatureWrapper(img.src, {width, height});
        });
      };

      this.typedText = '';
    },

    // Handle image upload
    async handleImageUpload(e) {
      if (this.isLoading) return;
      const file = e.target.files[0];
      if (!file) return;

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);

        this.$nextTick(() => {
          const {width, height} = this.getSignatureDisplaySize(
            img.width,
            img.height,
          );
          this.appendSignatureWrapper(canvas.toDataURL(), {width, height});
        });
      };

      img.src = URL.createObjectURL(file);
    },

    // Download signed PDF
    async downloadPDF() {
      if (this.isLoading) return;
      if (!this.pdfBytes) {
        await this.$toast.error({
          title: this.$t('general.error'),
          message: this.$t('auth.contract_empty_pdf'),
        });
        return;
      }
      const pdfDoc = await PDFDocument.load(this.pdfBytes);
      const pages = pdfDoc.getPages();

      for (const sig of this.signatureRenders) {
        const img = await pdfDoc.embedPng(sig.img.src);
        const page = pages[sig.page - 1];
        const {width, height} = page.getSize();
        const w = sig.width * width;
        const h = sig.height * height;

        if (sig.rotation) {
          // Calculate the center point in PDF coordinates
          const centerX = (sig.x + sig.width / 2) * width;
          const centerY = height - (sig.y + sig.height / 2) * height;

          // Negate rotation because PDF Y-axis is flipped compared to canvas on renderOverlay() function
          const pdfRotation = -sig.rotation;

          const cos = Math.cos(pdfRotation);
          const sin = Math.sin(pdfRotation);

          // Offset from center to bottom-left corner before rotation
          const dx = -w / 2;
          const dy = -h / 2;

          // Apply rotation to find where bottom-left corner should be placed
          const rotatedDx = dx * cos - dy * sin;
          const rotatedDy = dx * sin + dy * cos;

          const x = centerX + rotatedDx;
          const y = centerY + rotatedDy;

          // Convert radians to degrees (negated)
          const rotationInDegrees = (pdfRotation * 180) / Math.PI;

          page.drawImage(img, {
            x,
            y,
            width: w,
            height: h,
            rotate: degrees(rotationInDegrees),
          });
        } else {
          //  Non-rotated images, need to use top-left corner positioning
          const x = sig.x * width;
          const y = height - (sig.y + sig.height) * height;
          page.drawImage(img, {x, y, width: w, height: h});
        }
      }

      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes], {type: 'application/pdf'});
      const baseName = this.contract.oldAttachment.filename.replace(
        /\.pdf$/i,
        '',
      );
      const fileName = `${baseName}.pdf`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
    },

    // Download signed PDF
    async submitPDF() {
      if (this.signatureRenders.length < 1) {
        await this.$toast.info({
          title: this.$t('general.warning'),
          message: this.$t('auth.contract_empty_sign'),
        });
        return;
      }
      if (!this.pdfBytes) {
        await this.$toast.error({
          title: this.$t('general.error'),
          message: this.$t('auth.contract_empty_pdf'),
        });
        return;
      }
      const pdfDoc = await PDFDocument.load(this.pdfBytes);
      const pages = pdfDoc.getPages();

      for (const sig of this.signatureRenders) {
        const img = await pdfDoc.embedPng(sig.img.src);
        const page = pages[sig.page - 1];
        const {width, height} = page.getSize();
        const w = sig.width * width;
        const h = sig.height * height;

        // Logic same like downloaded the pdf
        if (sig.rotation) {
          const centerX = (sig.x + sig.width / 2) * width;
          const centerY = height - (sig.y + sig.height / 2) * height;
          const pdfRotation = -sig.rotation;
          const cos = Math.cos(pdfRotation);
          const sin = Math.sin(pdfRotation);
          const dx = -w / 2;
          const dy = -h / 2;
          const rotatedDx = dx * cos - dy * sin;
          const rotatedDy = dx * sin + dy * cos;
          const x = centerX + rotatedDx;
          const y = centerY + rotatedDy;
          const rotationInDegrees = (pdfRotation * 180) / Math.PI;

          page.drawImage(img, {
            x,
            y,
            width: w,
            height: h,
            rotate: degrees(rotationInDegrees),
          });
        } else {
          const x = sig.x * width;
          const y = height - (sig.y + sig.height) * height;
          page.drawImage(img, {x, y, width: w, height: h});
        }
      }

      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes], {type: 'application/pdf'});

      // Convert blob to Base64
      const base64PDF = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]); // this will remove "data:application/pdf;base64,"
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const baseName = this.contract.oldAttachment.filename
        .replace(/\.pdf$/i, '')
        .replace(/-signed.*$/i, '');
      const dateStr = moment().format('DD-MMM-YYYY');
      const signedFileName = `${baseName}-signed-${dateStr}.pdf`;

      this.contract.newAttachment = {
        name: signedFileName,
        type: blob.type,
        size: blob.size,
        base64: base64PDF,
      };

      this.submitContract();
    },
  },
};
</script>

<style src="./sign-contract.scss" lang="scss" scoped></style>
